import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Lock, Loader2, ShieldCheck } from "lucide-react";
import { useCustomerAuth } from "../context/CustomerAuthContext";

const passwordRegex = /(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}/;

export default function ResetPassword() {
  const { resetPassword } = useCustomerAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Cynos Reset Password";
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!passwordRegex.test(password)) {
      setError("Password must be at least 8 characters and include uppercase, lowercase, and a number.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await resetPassword(password);
      setSuccess("Password reset successfully. Please sign in.");
      setTimeout(() => navigate("/signin", { replace: true }), 1400);
    } catch (err) {
      const message = err.response?.data?.detail || err.message || "Unable to reset password.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-black px-4 py-24 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="mx-auto max-w-md rounded-3xl border border-white/10 bg-cynos-card p-8 shadow-2xl shadow-black/20"
      >
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center rounded-full bg-cynos-red/10 p-4 mb-4">
            <Lock className="h-6 w-6 text-cynos-red" />
          </div>
          <h1 className="text-3xl font-anton uppercase text-white">Reset Password</h1>
          <p className="mt-3 text-sm text-zinc-400">Create a new password and sign in to your Cynos account.</p>
        </div>

        {error && <div className="mb-6 rounded-2xl bg-red-500/15 px-4 py-3 text-sm text-red-200">{error}</div>}
        {success && <div className="mb-6 rounded-2xl bg-emerald-500/15 px-4 py-3 text-sm text-emerald-200">{success}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-zinc-400">New Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-2xl border border-white/10 bg-black/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cynos-red"
              placeholder="Create a strong password"
            />
          </div>
          <div>
            <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-zinc-400">Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full rounded-2xl border border-white/10 bg-black/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cynos-red"
              placeholder="Re-enter your password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-cynos-red px-4 py-3 text-sm uppercase tracking-[0.2em] text-black transition hover:bg-cynos-red/90 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? "Resetting" : "Reset Password"}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-zinc-400">
          <Link to="/signin" className="underline hover:text-white">Back to Sign In</Link>
        </div>
      </motion.div>
    </main>
  );
}
